
<script lang="ts">
    import {Button} from "@/components/ui/button";
    import Modal from "@/components/Modal.svelte";
    import FileUpload from "@/components/Inputs/FileUpload.svelte";
    import {toast} from "svelte-sonner";
    import { meta } from '@/meta.svelte';
    import type {Image} from "@/types/types";
    import ImageModal from "@/components/ImageModal.svelte";
    import { inertia, router, page } from '@inertiajs/svelte';
    import { Plus, ImageIcon } from 'lucide-svelte';
    import * as Card from '@/components/ui/card';
    import { untrack } from 'svelte';

    type ImageWithSource = Image & {
        source?: string; // e.g., 'Site Survey', 'Lead'
    };

    type Props = {
        relatedRecordId?: string | null;
        relatedRecordType?: string | null;
        images?: ImageWithSource[];
        onUploadComplete?: (uploadedImages: Image[]) => void;
        csrfToken?: string;
        showSourceBadge?: boolean; // Whether to show the source badge on images
        id?: string;
    };

    let { relatedRecordId = null, relatedRecordType = null, images = [], onUploadComplete, csrfToken, showSourceBadge = false, id }: Props = $props();

    // Local state for images - allows immediate updates without prop changes
    let localImages = $state<ImageWithSource[]>([...images]);

    // Sync with images prop changes
    $effect(() => {
        localImages = [...images];
    });

    // Separate effect for handling flash data (no dependencies on localImages)
    $effect(() => {
        const flash = $page.props.flash as any;
        if (!flash) return;

        // Use untrack to read localImages without creating a dependency
        untrack(() => {
            // Handle deleted image
            if (flash.deletedImageId) {
                localImages = localImages.filter(img => img.id !== flash.deletedImageId);
                return;
            }
        });
    });

    // let columns: number = 4;
    let gap: string = "1rem";

    let acceptedFileTypes = ["image/*"];

    let selectedIndex: number | null = $state(null);
    let selected = $derived(selectedIndex !== null ? localImages[selectedIndex] : null);

    function closeFullImageView() {
        selectedIndex = null;
    }

    function nextImage() {
        if (selectedIndex !== null && selectedIndex < localImages.length - 1) {
            selectedIndex = selectedIndex + 1;
        }
    }

    function previousImage() {
        if (selectedIndex !== null && selectedIndex > 0) {
            selectedIndex = selectedIndex - 1;
        }
    }

    function handleDescriptionUpdate(imageId: string, description: string | null) {
        localImages = localImages.map(img =>
            img.id === imageId ? { ...img, description } : img
        );
    }

    let showUpload = $state(false);
    function showFileUpload() {
        // Blur any focused element to prevent scroll jumping back to form fields
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        showUpload = true;
    }

    let fileUploadRef: any;
    let processingCount = $state(0);

    // Use CSRF token from Inertia props (falls back to DOM if not provided)
    const getCsrfToken = () => {
        return csrfToken || document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
    };

    const server = {
        url: '',
        process: {
            url: '/images/upload',
            method: 'POST',
            headers: (file: File) => {
                return {
                    'Upload-Name': file.name,
                    'X-CSRF-TOKEN': getCsrfToken(),
                };
            },
            withCredentials: false,
            ondata: (formData) => {
                formData.append('related_record_id', relatedRecordId);
                formData.append('related_record_type', relatedRecordType);
                return formData;
            },
            onload: (response) => {
                try {
                    const data = JSON.parse(response);
                    const uploadedImages = data.images || (data.image ? [data.image] : []);

                    localImages = [...localImages, ...uploadedImages];

                    // Notify parent component with uploaded images
                    if (onUploadComplete) {
                        onUploadComplete(uploadedImages);
                    }

                    toast.success("Upload complete");
                } catch (error) {
                    console.error('Failed to parse upload response:', error, response);
                    toast.error('Upload succeeded but failed to display image');

                    // Fallback to reload if parsing fails
                    if (!onUploadComplete) {
                        router.reload({ only: ['images'] });
                    }
                }
            },
            onerror: (response) => {
                toast.error('Upload failed');
            },
        },
        load: null,
        fetch: null,
        revert: null,
        restore: null,
    };
    function handleFileProcessed(error: any, file: any) {
        if (error) return;

        processingCount++;

        // fileUploadRef is the FileUpload component, which exposes getFiles()
        if (!fileUploadRef) return;

        const currentFiles = fileUploadRef.getFiles();
        const filesBeingProcessed = currentFiles.filter((f: any) => f.status === 5);
        if (filesBeingProcessed.length === currentFiles.length && currentFiles.length > 0) {
            setTimeout(() => {
                fileUploadRef?.reset();
                processingCount = 0;
            }, 100);
        }
    }
</script>

<style>
    .masonry {
        column-gap: var(--gap);
        column-width: 200px;
    }

    .masonry-item {
        break-inside: avoid;
        margin-bottom: var(--gap);
        display: block;
        position: relative;
        cursor: pointer;
    }

    .masonry-item img {
        width: 100%;
        display: block;
        border-radius: 0.5rem;
        transition: transform 0.2s ease;
    }

    .masonry-item:hover img {
        transform: scale(1.02);
    }

    .source-badge {
        position: absolute;
        top: 0.5rem;
        left: 0.5rem;
        padding: 0.25rem 0.5rem;
        border-radius: 0.375rem;
        font-size: 0.75rem;
        font-weight: 600;
        background: rgba(0, 0, 0, 0.7);
        color: white;
        backdrop-filter: blur(4px);
        z-index: 1;
    }

    .source-site-survey {
        background: rgba(59, 130, 246, 0.9); /* blue */
    }

    .source-lead {
        background: rgba(16, 185, 129, 0.9); /* green */
    }

    .source-scaffolding-handover {
        background: rgba(245, 158, 11, 0.9); /* amber/orange */
    }
</style>
<Card.Root {id}>
    <Card.Content>
<Card.Title class="flex items-center justify-between mb-2 text-lg">
    Gallery
    {#if !showUpload}
        <Button
            onclick={showFileUpload}
            class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent">
            <Plus class="h-4 w-4" />
            Upload Images
        </Button>
    {/if}
</Card.Title>
{#if showUpload}
    <FileUpload
        bind:this={fileUploadRef}
        {acceptedFileTypes}
        label="Upload Images"
        allowMultiple={true}
        server={server}
        maxFileSize="30MB"
        onProcessFile={handleFileProcessed}
    />
{/if}
{#if localImages.length > 0}
    <div
        class="masonry"
        style="
            --gap: {gap};"
    >
        {#each localImages as photo, index (photo.id)}
            <button class="masonry-item" onclick={() => (selectedIndex = index)}>
                {#if showSourceBadge && photo.source}
                    <div class="source-badge"
                         class:source-site-survey={photo.source === 'Site Survey'}
                         class:source-lead={photo.source === 'Lead'}
                         class:source-scaffolding-handover={photo.source === 'Scaffolding Handover'}>
                        {photo.source}
                    </div>
                {/if}
                <img src={`/images/preview/${photo.id}`} alt="Gallery preview"/>
            </button>
        {/each}

        {#if selected}
            <ImageModal
                title=""
                onClose={closeFullImageView}
                image={selected}
                onNext={selectedIndex !== null && selectedIndex < localImages.length - 1 ? nextImage : undefined}
                onPrevious={selectedIndex !== null && selectedIndex > 0 ? previousImage : undefined}
                currentIndex={selectedIndex}
                totalImages={localImages.length}
                onDescriptionUpdate={handleDescriptionUpdate}
            />
        {/if}
    </div>
{:else if !showUpload}
    <div class="flex flex-col items-center justify-center py-8 text-center">
        <ImageIcon class="h-12 w-12 text-muted-foreground/50 mb-3" />
        <p class="text-muted-foreground text-sm">No images uploaded yet</p>
        <p class="text-muted-foreground text-xs mt-1">Click "Upload Images" to add some</p>
    </div>
{/if}
    </Card.Content>
</Card.Root>
